import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import ga from 'src/utils/pixels/gtag'
import { getSubmitLocation } from 'src/utils/submitLocation'
import { useAuthProvider } from '../../context/AuthProvider'
import { RedirectUrls } from '../../types/types'
import BaseButton from '../Buttons/BaseButton'
import LanguageDropdown from './LanguageDropdown'

type IProp = {
  hideLinks?: boolean
  isAnfitrion?: boolean
}
const ProfileButton = dynamic(() => import('../ProfileButton'))

const DesktopButtons = ({ hideLinks = false, isAnfitrion }: IProp) => {
  const { isAuthenticated, currentUser, loading } = useAuthProvider()
  const { t } = useTranslation('nav')
  const [isClient, setIsClient] = useState(false)

  // Prevent hydration mismatch by only rendering auth-dependent content on client
  useEffect(() => {
    setIsClient(true)
  }, [])

  const handleOnClick = () => {
    // Was `location: 'CTASection'` — a copy-paste from the (now dead)
    // Landing/CTASection component. This is the desktop nav CTA and was the
    // only live emitter of that value, so every historical `CTASection` fire
    // is really this button.
    ga.event('anfitrion_click', { location: 'nav_desktop' })
  }

  const ctaHref = isAnfitrion
    ? getSubmitLocation(currentUser)
    : RedirectUrls.Anfitrion

  return (
    <div className="hidden items-center gap-2 md:flex lg:gap-3">
      {/* Primary CTA */}
      <BaseButton
        href={ctaHref}
        variant="contained"
        rounded
        onClick={handleOnClick}
        blueBorder
        className={`text-nowrap transition-transform hover:scale-[1.02] ${hideLinks ? 'hidden lg:block md-lg:hidden' : ''}`}
      >
        {t('NAV.BUTTON_UPLOAD')}
      </BaseButton>

      {/* Profile button (authenticated) */}
      {isClient && !loading && isAuthenticated && (
        <div className="mx-1 block transition-opacity duration-200">
          <ProfileButton currentUser={currentUser} />
        </div>
      )}

      {/* Login button (not authenticated) — demoted to text-link.
          `mx-2 px-4` (was `mx-1 px-3`) gives ≥24px clearance to neighbouring
          controls to satisfy WCAG 2.5.8 minimum target spacing. */}
      {isClient && !loading && !isAuthenticated && (
        <BaseButton
          rounded
          variant="text"
          href={RedirectUrls.Login}
          sx={{ textWrap: 'nowrap' }}
          className="mx-2 block px-4 py-2 text-sm font-medium text-ink-700 hover:bg-cream-300 hover:text-ink-800"
        >
          {t('NAV.BUTTON_LOGIN')}
        </BaseButton>
      )}

      {/* Loading skeleton */}
      {(!isClient || loading) && (
        <div className="mx-1 block">
          <div className="h-[40px] w-[120px] animate-pulse rounded-3xl border border-ink-100/15 bg-cream-300" />
        </div>
      )}

      {/* Language dropdown */}
      <LanguageDropdown />
    </div>
  )
}

export default DesktopButtons
